import { Chip as MuiChip, ChipProps as MuiChipProps, Theme } from '@mui/material';

export type ChipProps = Omit<MuiChipProps, 'variant'> & {
  variant?: MuiChipProps['variant'] | 'muted';
};

export const Chip = ({ variant = 'filled', color = 'default', sx, ...props }: ChipProps) => {
  const isMuted = variant === 'muted';
  const mappedVariant: MuiChipProps['variant'] = isMuted ? 'filled' : variant;

  const baseSx = {
    p: '2px 6px',
  };

  const mutedFill =
    color === 'default'
      ? { color: 'text.secondary', backgroundColor: 'action.selected' }
      : { color: `${color}.main`, backgroundColor: `${color}.light` };

  // Deepens the tint with the chip's own colour; `action.*` is brand-tinted in these themes.
  const deepenedFill = (theme: Theme, inkShare: number) => {
    const palette = theme.palette as unknown as Record<string, { main: string; light: string }>;
    const [ink, fill] =
      color === 'default'
        ? [theme.palette.text.secondary, theme.palette.action.selected]
        : [palette[color].main, palette[color].light];
    return `color-mix(in srgb, ${ink} ${inkShare}%, ${fill})`;
  };

  // Interactive states would otherwise fall back to MUI's filled chip: a dark hover, a pale icon.
  const mutedSx = isMuted
    ? {
        ...mutedFill,
        '&.MuiChip-clickable:hover': {
          backgroundColor: (theme: Theme) => deepenedFill(theme, 12),
        },
        '&.MuiChip-clickable.Mui-focusVisible': {
          backgroundColor: (theme: Theme) => deepenedFill(theme, 20),
        },
        // MUI colours the delete icon from `contrastText`, meant for a `main` background.
        '& .MuiChip-deleteIcon': {
          color: 'inherit',
          opacity: 0.7,
          '&:hover': { color: 'inherit', opacity: 1 },
        },
      }
    : undefined;

  return (
    <MuiChip
      size="small"
      {...props}
      color={color}
      variant={mappedVariant}
      sx={[baseSx, mutedSx, ...(Array.isArray(sx) ? sx : [sx])]}
    />
  );
};
